<template>
  <div
    v-if="!widget"
    class="inspector inspector--empty"
    :class="{
      'inspector--empty-dock': layout === 'dock',
      'inspector--empty-side': layout === 'side',
    }"
  >
    点击 Dashboard 栅格中的控件以编辑属性
  </div>
  <div
    v-else
    class="inspector"
    :class="{ 'inspector--dock': layout === 'dock', 'inspector--side': layout === 'side' }"
  >
    <header class="in-hero">
      <div class="in-hero-title">控件属性</div>
      <div class="in-hero-type">{{ labels[widget.type] }} · {{ widget.type }}</div>
    </header>

    <div class="in-scroll">
      <section v-if="posPx" class="in-card">
        <div class="in-card-hd">布局</div>
        <div class="pos-grid">
          <label class="fld fld--pos">
            <span>X</span>
            <input
              type="number"
              class="inp inp-pos"
              :value="posPx.x"
              @change="commitPosPx('x', ($event.target as HTMLInputElement).value)"
              @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
            />
          </label>
          <label class="fld fld--pos">
            <span>Y</span>
            <input
              type="number"
              class="inp inp-pos"
              :value="posPx.y"
              @change="commitPosPx('y', ($event.target as HTMLInputElement).value)"
              @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
            />
          </label>
          <label class="fld fld--pos">
            <span>W</span>
            <input
              type="number"
              class="inp inp-pos"
              :value="posPx.w"
              @change="commitPosPx('w', ($event.target as HTMLInputElement).value)"
              @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
            />
          </label>
          <label class="fld fld--pos">
            <span>H</span>
            <input
              type="number"
              class="inp inp-pos"
              :value="posPx.h"
              @change="commitPosPx('h', ($event.target as HTMLInputElement).value)"
              @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
            />
          </label>
        </div>
      </section>

      <section class="in-card">
        <div class="in-card-hd">外观主题</div>
        <select class="inp" :value="widget.theme ?? ''" @change="onTheme($event)">
          <option value="">{{ WIDGET_THEME_LABELS.dark }}（默认）</option>
          <option v-for="id in themeableIds" :key="id" :value="id">{{ WIDGET_THEME_LABELS[id] }}</option>
        </select>
      </section>

      <section class="in-card">
        <div class="in-card-hd">文案与字号</div>
        <div
          class="in-pair"
          :class="{ 'in-pair--stack': widget.type !== 'ValueCard' }"
        >
          <label class="fld">
            <span>标题 / 标签</span>
            <input v-model="labelLocal" type="text" class="inp" @change="commit({ label: labelLocal })" />
          </label>
          <label v-if="widget.type === 'ValueCard'" class="fld">
            <span>单位</span>
            <input v-model="unitLocal" type="text" class="inp" @change="commit({ unit: unitLocal })" />
          </label>
        </div>
        <div
          v-if="showFontControls"
          class="in-font-grid"
          :class="{ 'in-font-grid--single': fontControlsSingleColumn }"
        >
          <label v-if="widgetHasContentFontControl(widget.type)" class="fld">
            <span>主内容字号</span>
            <select class="inp" :value="fontSizeSelectValue" @change="onFontSizeSelect($event)">
              <option value="">{{ autoContentFontLabel }}</option>
              <option v-if="savedOrphanContentPx != null" :value="String(savedOrphanContentPx)">
                {{ savedOrphanContentPx }} px（已保存）
              </option>
              <option v-for="n in DASHBOARD_FONT_SIZE_PRESETS" :key="'c-' + n" :value="String(n)">{{ n }} px</option>
            </select>
          </label>
          <label v-if="widgetHasLabelFontControl(widget.type)" class="fld">
            <span>{{ widget.type === 'ToggleSwitch' ? '说明文字字号' : '标题字号' }}</span>
            <select class="inp" :value="labelFontSelectValue" @change="onLabelFontSizeSelect($event)">
              <option value="">{{ autoLabelFontLabel }}</option>
              <option v-if="savedOrphanLabelPx != null" :value="String(savedOrphanLabelPx)">
                {{ savedOrphanLabelPx }} px（已保存）
              </option>
              <option v-for="n in DASHBOARD_FONT_SIZE_PRESETS" :key="'l-' + n" :value="String(n)">{{ n }} px</option>
            </select>
          </label>
        </div>
      </section>

      <section v-if="needsBinding || widget.type === 'AlarmList'" class="in-card">
        <div class="in-card-hd">数据绑定</div>
        <div v-if="needsBinding" class="in-bind-grid">
          <label class="fld">
            <span>sourceKey</span>
            <select class="inp" :value="widget.binding?.sourceKey ?? ''" @change="onSourceKey($event)">
              <option value="">（未绑定）</option>
              <option v-for="opt in sourceKeyOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
            </select>
          </label>
          <label class="fld fld--field-stack">
            <span>field</span>
            <select
              v-if="bindingFieldCandidates.length"
              class="inp inp-field-preset"
              :value="fieldPresetSentinel"
              @change="onFieldPresetPick($event)"
            >
              <option :value="fieldPresetSentinel">从场景信息卡绑定选择…</option>
              <option v-for="c in bindingFieldCandidates" :key="c.field" :value="c.field">
                {{ c.field }}（{{ c.label }}）
              </option>
            </select>
            <datalist v-if="bindingFieldCandidates.length" id="dash-widget-field-datalist">
              <option v-for="c in bindingFieldCandidates" :key="'dl-' + c.field" :value="c.field" />
            </datalist>
            <input
              v-model="fieldLocal"
              type="text"
              class="inp"
              placeholder="如 temperature"
              :list="bindingFieldCandidates.length ? 'dash-widget-field-datalist' : undefined"
              @change="commitBindingPartial({ field: fieldLocal })"
            />
          </label>
        </div>
        <label v-if="widget.type === 'AlarmList'" class="fld fld--tight-top">
          <span>告警范围</span>
          <select class="inp" :value="widget.alarmScope ?? 'global'" @change="onAlarmScope($event)">
            <option value="global">全局</option>
            <option v-for="opt in sourceKeyOptions" :key="'a-' + opt.key" :value="opt.key">{{ opt.label }}</option>
          </select>
        </label>
      </section>

      <section v-if="widget.type === 'Gauge' || widget.type === 'TrendLine'" class="in-card">
        <div class="in-card-hd">图表参数</div>
        <div v-if="widget.type === 'Gauge'" class="in-cols-2">
          <label class="fld">
            <span>最小值</span>
            <input v-model.number="gaugeMin" type="number" class="inp" @change="commitStyleOverrides" />
          </label>
          <label class="fld">
            <span>最大值</span>
            <input v-model.number="gaugeMax" type="number" class="inp" @change="commitStyleOverrides" />
          </label>
        </div>
        <label v-if="widget.type === 'TrendLine'" class="fld fld--tight-top">
          <span>最大点数</span>
          <input
            v-model.number="trendMaxPoints"
            type="number"
            min="8"
            max="256"
            class="inp"
            @change="commitStyleOverrides"
          />
        </label>
      </section>

      <section v-if="widget.type === 'ValueCard' || widget.type === 'Gauge'" class="in-card">
        <div class="in-card-hd">阈值配色</div>
        <p class="in-hint">按数值升序，满足则采用对应颜色</p>
        <div v-for="(th, i) in thresholdsLocal" :key="i" class="th-row">
          <input v-model.number="th.value" type="number" class="inp inp-sm" @change="commitThresholds" />
          <input v-model="th.color" type="color" class="color-inp" @change="commitThresholds" />
          <button type="button" class="btn-x" @click="removeThreshold(i)">×</button>
        </div>
        <button type="button" class="btn-add" @click="addThreshold">+ 添加阈值</button>
      </section>

      <section v-if="widget.type === 'FocusButton' || hasAction" class="in-card">
        <div class="in-card-hd">交互与下发</div>
        <label v-if="widget.type === 'FocusButton'" class="fld">
          <span>聚焦对象</span>
          <select class="inp" :value="widget.focusObjectId ?? ''" @change="onFocusObject($event)">
            <option value="">（未设置）</option>
            <option v-for="o in objects" :key="o.id" :value="o.id">{{ o.name || o.id }}</option>
          </select>
        </label>
        <template v-if="hasAction">
          <div class="in-action-head">下发动作</div>
          <div class="in-cols-2">
            <label class="fld">
              <span>通道</span>
              <select class="inp" :value="actionTransport" @change="onActionTransport($event)">
                <option value="mqtt">mqtt</option>
                <option value="websocket">websocket</option>
                <option value="http">http</option>
              </select>
            </label>
            <label v-if="actionTransport === 'http'" class="fld">
              <span>URL</span>
              <input v-model="actionUrl" type="text" class="inp" @change="commitAction" />
            </label>
            <label v-else class="fld">
              <span>Topic</span>
              <input v-model="actionTopic" type="text" class="inp" @change="commitAction" />
            </label>
          </div>
          <label class="fld chk fld--inline">
            <input v-model="actionConfirm" type="checkbox" @change="commitAction" />
            <span>需要确认</span>
          </label>
          <label class="fld">
            <span>Payload JSON</span>
            <textarea v-model="actionPayloadText" class="ta" rows="4" spellcheck="false" @change="commitActionPayload" />
            <span v-if="actionPayloadError" class="fld-err">{{ actionPayloadError }}</span>
          </label>
        </template>
      </section>
    </div>

    <footer class="in-footer">
      <div class="action-order-row">
        <button type="button" class="btn-order" title="移到最顶层（最后渲染）" @click="emit('reorder-widget', 'front')">前置</button>
        <button type="button" class="btn-order" title="移到最底层（最先渲染）" @click="emit('reorder-widget', 'back')">后置</button>
      </div>
      <button type="button" class="btn-del" @click="emit('delete-widget')">删除控件</button>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import type { SceneObject, SceneData } from '@/types/scene'
import type { WidgetDef, WidgetTheme, WidgetThreshold, WidgetTransportAction } from '@/types/dashboardConfig'
import { WIDGET_THEME_IDS, WIDGET_THEME_LABELS } from '@/types/dashboardConfig'
import { dashboardMetricsBucketKey } from '@/dashboard/dashboardMetricsBucketKey'
import { WIDGET_TYPE_LABELS } from './dashboardComposeDefaults'
import { normalizePosition } from '@/dashboard/widgetGridStyle'
import { clampWidgetPosition, MIN_FRAC } from '@/dashboard/editor/useDashboardGrid'
import { useSceneStore } from '@/stores/scene'
import { buildSceneDataKeyCatalog } from '@/utils/sceneDataKeyCatalog'
import {
  DASHBOARD_FONT_SIZE_PRESETS,
  isDashboardPresetFontSize,
  widgetHasContentFontControl,
  widgetHasLabelFontControl,
} from '@/dashboard/dashboardFontPresets'

const sceneStore = useSceneStore()
const { composeAutoResolvedFontPx, composeAutoResolvedLabelFontPx } = storeToRefs(sceneStore)

const autoContentFontLabel = computed(() => {
  const px = composeAutoResolvedFontPx.value
  return px != null ? `自动（约 ${px}px）` : '自动'
})

const autoLabelFontLabel = computed(() => {
  const px = composeAutoResolvedLabelFontPx.value
  return px != null ? `自动（约 ${px}px）` : '自动'
})

const props = withDefaults(
  defineProps<{
    widget: WidgetDef | null
    scene: SceneData | undefined
    layout?: 'float' | 'dock' | 'side'
    gridSize?: { w: number; h: number }
    /** 组态：与画布「网格吸附」一致，约束 position clamp 的最小宽高（比例） */
    positionClampMinFrac?: number
  }>(),
  { layout: 'float' }
)

const emit = defineEmits<{
  'update-widget': [patch: Partial<WidgetDef>]
  'delete-widget': []
  'reorder-widget': [direction: 'front' | 'back']
}>()

const labels = WIDGET_TYPE_LABELS
/** 下拉里「默认」选项单独渲染 dark，这里只列剩余三个可选主题 */
const themeableIds = WIDGET_THEME_IDS.filter((id) => id !== 'dark')

function onTheme(e: Event) {
  const v = (e.target as HTMLSelectElement).value as WidgetTheme | ''
  commit({ theme: v || undefined })
}

const posPx = computed(() => {
  const w = props.widget
  const gs = props.gridSize
  if (!w || !gs || gs.w <= 0) return null
  const pos = normalizePosition(w.position)
  return {
    x: Math.round(pos.x * gs.w),
    y: Math.round(pos.y * gs.h),
    w: Math.round(pos.w * gs.w),
    h: Math.round(pos.h * gs.h),
  }
})

function commitPosPx(field: 'x' | 'y' | 'w' | 'h', rawVal: string) {
  const val = parseInt(rawVal, 10)
  if (isNaN(val) || !props.widget || !props.gridSize) return
  const { w: uw, h: uh } = props.gridSize
  if (uw <= 0 || uh <= 0) return
  const pos = normalizePosition(props.widget.position)
  const updated = { ...pos }
  if (field === 'x') updated.x = val / uw
  else if (field === 'y') updated.y = val / uh
  else if (field === 'w') updated.w = val / uw
  else updated.h = val / uh
  emit('update-widget', {
    position: clampWidgetPosition(updated, { minFrac: props.positionClampMinFrac ?? MIN_FRAC }),
  })
}

const objects = computed(() => props.scene?.objects ?? [])

const sourceKeyOptions = computed(() =>
  objects.value.map((o: SceneObject) => ({
    key: dashboardMetricsBucketKey(o),
    label: `${o.name || o.id} → ${dashboardMetricsBucketKey(o)}`,
  }))
)

const fieldPresetSentinel = '__field_preset__'

const bindingFieldCandidates = computed(() => {
  const sk = props.widget?.binding?.sourceKey?.trim()
  if (!sk) return []
  const { fieldCandidatesByBucketKey } = buildSceneDataKeyCatalog(objects.value)
  return fieldCandidatesByBucketKey[sk] ?? []
})

const needsBinding = computed(() =>
  props.widget
    ? ['ValueCard', 'Gauge', 'StatusBadge', 'TrendLine', 'ToggleSwitch'].includes(props.widget.type)
    : false
)

const hasAction = computed(() =>
  props.widget ? ['ControlButton', 'ToggleSwitch'].includes(props.widget.type) : false
)

const showFontControls = computed(() => {
  const w = props.widget
  if (!w) return false
  return widgetHasContentFontControl(w.type) || widgetHasLabelFontControl(w.type)
})

/** 仅主内容或仅标题其一可调时，字号区单列排布 */
const fontControlsSingleColumn = computed(() => {
  const w = props.widget
  if (!w) return true
  const c = widgetHasContentFontControl(w.type)
  const l = widgetHasLabelFontControl(w.type)
  return c !== l
})

const savedOrphanContentPx = computed(() => {
  const w = props.widget
  if (!w || !widgetHasContentFontControl(w.type)) return null
  const fs = Number(w.styleOverrides?.fontSize)
  if (!Number.isFinite(fs) || fs < 8) return null
  const rounded = Math.round(fs)
  return isDashboardPresetFontSize(rounded) ? null : rounded
})

const savedOrphanLabelPx = computed(() => {
  const w = props.widget
  if (!w || !widgetHasLabelFontControl(w.type)) return null
  const lf = Number(w.styleOverrides?.labelFontSize)
  const fs = Number(w.styleOverrides?.fontSize)
  if (w.type === 'ToggleSwitch') {
    const src = Number.isFinite(lf) && lf >= 8 ? lf : fs
    if (!Number.isFinite(src) || src < 8) return null
    const rounded = Math.round(src)
    return isDashboardPresetFontSize(rounded) ? null : rounded
  }
  if (!Number.isFinite(lf) || lf < 8) return null
  const rounded = Math.round(lf)
  return isDashboardPresetFontSize(rounded) ? null : rounded
})

const labelLocal = ref('')
const unitLocal = ref('')
const fieldLocal = ref('')
const fontSizeSelectValue = ref('')
const labelFontSelectValue = ref('')
const thresholdsLocal = ref<WidgetThreshold[]>([])
const gaugeMin = ref(0)
const gaugeMax = ref(100)
const trendMaxPoints = ref(32)
const actionTransport = ref<WidgetTransportAction['transport']>('mqtt')
const actionTopic = ref('')
const actionUrl = ref('')
const actionConfirm = ref(false)
const actionPayloadText = ref('{}')
const actionPayloadError = ref('')

watch(
  () => props.widget,
  (w) => {
    if (!w) return
    labelLocal.value = w.label ?? ''
    unitLocal.value = w.unit ?? ''
    fieldLocal.value = w.binding?.field ?? ''
    thresholdsLocal.value = w.thresholds ? [...w.thresholds] : []
    const so = w.styleOverrides ?? {}
    gaugeMin.value = Number(so.min) || 0
    gaugeMax.value = Number(so.max) || 100
    trendMaxPoints.value = Number(so.maxPoints) || 32

    const fs = Number(so.fontSize)
    const lf = Number(so.labelFontSize)

    if (widgetHasContentFontControl(w.type)) {
      fontSizeSelectValue.value = Number.isFinite(fs) && fs >= 8 ? String(Math.round(fs)) : ''
    } else {
      fontSizeSelectValue.value = ''
    }

    if (widgetHasLabelFontControl(w.type)) {
      if (w.type === 'ToggleSwitch') {
        const pick = Number.isFinite(lf) && lf >= 8 ? lf : fs
        labelFontSelectValue.value = Number.isFinite(pick) && pick >= 8 ? String(Math.round(pick)) : ''
      } else {
        labelFontSelectValue.value = Number.isFinite(lf) && lf >= 8 ? String(Math.round(lf)) : ''
      }
    } else {
      labelFontSelectValue.value = ''
    }

    const a = w.action
    actionTransport.value = a?.transport ?? 'mqtt'
    actionTopic.value = a?.topic ?? 'factory/device/${assetCode}/cmd'
    actionUrl.value = a?.url ?? ''
    actionConfirm.value = !!a?.confirmRequired
    try {
      actionPayloadText.value = JSON.stringify(a?.payload ?? { cmd: 'ping' }, null, 2)
    } catch {
      actionPayloadText.value = '{}'
    }
  },
  { immediate: true }
)

function commit(patch: Partial<WidgetDef>) {
  emit('update-widget', patch)
}

function commitBindingPartial(p: Partial<{ sourceKey: string; field: string }>) {
  const w = props.widget
  if (!w) return
  const cur = w.binding ?? { sourceKey: '', field: '' }
  emit('update-widget', { binding: { ...cur, ...p } })
}

function onSourceKey(e: Event) {
  const v = (e.target as HTMLSelectElement).value
  commitBindingPartial({ sourceKey: v })
}

function onFieldPresetPick(e: Event) {
  const sel = e.target as HTMLSelectElement
  const v = sel.value
  if (!v || v === fieldPresetSentinel) return
  fieldLocal.value = v
  commitBindingPartial({ field: v })
  sel.value = fieldPresetSentinel
}

function onAlarmScope(e: Event) {
  const v = (e.target as HTMLSelectElement).value as 'global' | string
  commit({ alarmScope: v === 'global' ? 'global' : v })
}

function onFocusObject(e: Event) {
  const v = (e.target as HTMLSelectElement).value
  commit({ focusObjectId: v || undefined })
}

function commitThresholds() {
  emit('update-widget', { thresholds: [...thresholdsLocal.value] })
}

function addThreshold() {
  thresholdsLocal.value = [...thresholdsLocal.value, { value: 0, color: '#faad14' }]
  commitThresholds()
}

function removeThreshold(i: number) {
  thresholdsLocal.value = thresholdsLocal.value.filter((_, j) => j !== i)
  commitThresholds()
}

function patchFontStyleKeys(base: Record<string, unknown>) {
  const w = props.widget
  if (!w) return
  if (widgetHasContentFontControl(w.type)) {
    const v = fontSizeSelectValue.value
    if (v === '') delete base.fontSize
    else {
      const n = parseInt(v, 10)
      if (Number.isFinite(n) && n >= 8) base.fontSize = n
    }
  }
  if (widgetHasLabelFontControl(w.type)) {
    const v = labelFontSelectValue.value
    if (v === '') {
      delete base.labelFontSize
      if (w.type === 'ToggleSwitch') delete base.fontSize
    } else {
      const n = parseInt(v, 10)
      if (Number.isFinite(n) && n >= 8) {
        base.labelFontSize = n
        if (w.type === 'ToggleSwitch') delete base.fontSize
      }
    }
  }
}

function onFontSizeSelect(e: Event) {
  fontSizeSelectValue.value = (e.target as HTMLSelectElement).value
  commitStyleOverrides()
}

function onLabelFontSizeSelect(e: Event) {
  labelFontSelectValue.value = (e.target as HTMLSelectElement).value
  commitStyleOverrides()
}

function commitStyleOverrides() {
  const w = props.widget
  if (!w) return
  const base = { ...(w.styleOverrides ?? {}) }
  if (w.type === 'Gauge') {
    base.min = gaugeMin.value
    base.max = gaugeMax.value
  }
  if (w.type === 'TrendLine') {
    base.maxPoints = trendMaxPoints.value
  }
  patchFontStyleKeys(base)
  emit('update-widget', { styleOverrides: base })
}

function onActionTransport(e: Event) {
  actionTransport.value = (e.target as HTMLSelectElement).value as WidgetTransportAction['transport']
  commitAction()
}

function commitAction() {
  let payload: Record<string, unknown> = {}
  try {
    payload = JSON.parse(actionPayloadText.value || '{}') as Record<string, unknown>
    actionPayloadError.value = ''
  } catch {
    actionPayloadError.value = 'JSON 格式错误，已保留上次有效值'
    return
  }
  const action: WidgetTransportAction = {
    transport: actionTransport.value,
    payload,
    confirmRequired: actionConfirm.value,
  }
  if (actionTransport.value === 'http') {
    action.url = actionUrl.value
  } else {
    action.topic = actionTopic.value
  }
  emit('update-widget', { action })
}

function commitActionPayload() {
  commitAction()
}
</script>

<style scoped>
.inspector {
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(51, 65, 85, 0.7);
  border-radius: 10px;
  padding: 0;
  width: 100%;
  max-width: 300px;
  max-height: min(72vh, 560px);
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
}
.inspector--dock {
  max-width: none;
  border-radius: 0;
  border: none;
  border-top: 1px solid rgba(51, 65, 85, 0.65);
  box-shadow: none;
  max-height: min(38vh, 420px);
  background: rgba(15, 23, 42, 0.97);
}
.inspector--empty {
  font-size: 11px;
  color: #94a3b8;
  padding: 8px 12px;
  width: 100%;
  max-width: 200px;
  line-height: 1.4;
  display: block;
  overflow: visible;
}
.inspector--empty-dock {
  max-width: none;
  width: 100%;
  box-sizing: border-box;
  padding: 6px 10px;
  text-align: center;
  border: none;
  border-top: 1px solid rgba(51, 65, 85, 0.45);
  background: rgba(15, 23, 42, 0.55);
  box-shadow: none;
}
.inspector--side {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  max-height: none;
  flex: 1 1 0%;
  height: 100%;
  min-height: 0;
  min-width: 0;
  border-radius: 0;
  border: none;
  box-shadow: none;
  background: rgba(15, 23, 42, 0.92);
}
.inspector.inspector--empty.inspector--empty-side {
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  max-height: none;
  flex: 1 1 0%;
  height: 100%;
  min-height: 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: rgba(15, 23, 42, 0.92);
}

.in-hero {
  flex-shrink: 0;
  padding: 12px 12px 8px;
  border-bottom: 1px solid rgba(51, 65, 85, 0.4);
}
.in-hero-title {
  font-size: 13px;
  font-weight: 600;
  color: #e2e8f0;
}
.in-hero-type {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.in-scroll {
  flex: 1 1 0%;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.in-card {
  min-width: 0;
  background: rgba(30, 41, 59, 0.42);
  border: 1px solid rgba(71, 85, 105, 0.38);
  border-radius: 8px;
  padding: 10px 10px 8px;
}
.in-card-hd {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #64748b;
  text-transform: uppercase;
  margin: 0 0 8px;
}

.in-hint {
  margin: -4px 0 8px;
  font-size: 10px;
  color: #64748b;
  line-height: 1.35;
}

.in-pair {
  display: grid;
  gap: 8px;
  min-width: 0;
}
.in-pair:not(.in-pair--stack) {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}
.in-font-grid {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(71, 85, 105, 0.35);
}
.in-font-grid--single {
  grid-template-columns: minmax(0, 1fr);
}
@media (max-width: 260px) {
  .in-pair:not(.in-pair--stack) {
    grid-template-columns: minmax(0, 1fr);
  }
  .in-font-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.in-bind-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.in-cols-2 {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
}

.in-action-head {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  margin: 8px 0 6px;
  padding-top: 4px;
  border-top: 1px dashed rgba(71, 85, 105, 0.35);
}
.in-card .in-action-head:first-child {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.fld {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 0;
  font-size: 11px;
  color: #94a3b8;
}
.fld--tight-top {
  margin-top: 8px;
}
.fld.chk {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
.fld--inline {
  margin-top: 6px;
  margin-bottom: 4px;
}
.inp {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 6px 8px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid rgba(71, 85, 105, 0.6);
  background: rgba(30, 41, 59, 0.9);
  color: #e2e8f0;
}
.inp-sm {
  width: 72px;
}
.ta {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  font-family: ui-monospace, monospace;
  font-size: 11px;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid rgba(71, 85, 105, 0.6);
  background: rgba(15, 23, 42, 0.95);
  color: #e2e8f0;
  resize: vertical;
}
.th-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.color-inp {
  width: 36px;
  height: 28px;
  padding: 0;
  border: none;
  cursor: pointer;
}
.btn-x {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid #475569;
  background: #1e293b;
  color: #e2e8f0;
  cursor: pointer;
}
.btn-add {
  font-size: 11px;
  padding: 4px 8px;
  margin-bottom: 4px;
  border-radius: 6px;
  border: 1px dashed #64748b;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
}
.fld-err {
  font-size: 10px;
  color: #f87171;
  margin-top: 2px;
}

.in-footer {
  flex-shrink: 0;
  padding: 10px 12px 12px;
  border-top: 1px solid rgba(51, 65, 85, 0.5);
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: rgba(15, 23, 42, 0.6);
}
.action-order-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}
.btn-order {
  padding: 6px 0;
  font-size: 11px;
  border-radius: 6px;
  border: 1px solid rgba(71, 85, 105, 0.6);
  background: rgba(30, 41, 59, 0.8);
  color: #94a3b8;
  cursor: pointer;
}
.btn-order:hover {
  border-color: rgba(96, 165, 250, 0.5);
  color: #bfdbfe;
  background: rgba(51, 65, 85, 0.5);
}
.btn-del {
  width: 100%;
  padding: 8px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid #7f1d1d;
  background: rgba(127, 29, 29, 0.25);
  color: #fecaca;
  cursor: pointer;
}
.btn-del:hover {
  background: rgba(127, 29, 29, 0.45);
}
.pos-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 6px;
}
.fld--pos {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.05em;
}
.inp-pos {
  text-align: right;
  width: 100%;
  box-sizing: border-box;
  appearance: textfield;
}
.inp-pos::-webkit-inner-spin-button,
.inp-pos::-webkit-outer-spin-button {
  opacity: 0.4;
}
</style>
